import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLighthouseAgents } from "@/hooks/security/useLighthouseData";
import { useCreateApproval } from "@/hooks/security/useApprovals";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RequestApprovalDialog({ open, onOpenChange }: Props) {
  const agents = useLighthouseAgents();
  const [agentId, setAgentId] = useState<string>("");
  const [reason, setReason] = useState("");
  const mut = useCreateApproval();

  const selected = agents.data?.find((a) => a.id === agentId);

  const submit = () => {
    if (!agentId || !reason.trim()) return;
    mut.mutate(
      {
        agentId,
        publisher: selected?.vendor,
        requestReason: reason,
      },
      {
        onSuccess: () => {
          setAgentId("");
          setReason("");
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request approval</DialogTitle>
          <DialogDescription>
            Pre-emptively raise an approval for an agent you expect to see on the fleet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Agent</label>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select an agent…" />
              </SelectTrigger>
              <SelectContent>
                {(agents.data ?? []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} · {a.vendor}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Reason</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Pre-emptive raise per Q3 governance review"
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!agentId || !reason.trim() || mut.isPending}>
            Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
